import { useRef, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { getGoogleAccessToken, setGoogleAccessToken, isGoogleTokenExpiring } from '@/lib/cookies';
import { setGoogleAccessToken as setTokenInService } from '@/features/blog/services/imageUploadService';
import { googleDriveAPI } from '@/features/auth/api/googleDriveApi';
import { toast } from 'sonner';
import { useAppDispatch } from '@/store/hooks';
import { setGoogleDriveConnection } from '@/features/auth/store/authSlice';

/**
 * Hook to handle Google Drive authentication with Refresh Token
 * Uses Authorization Code Flow + Backend to manage refresh tokens
 */
export const useGoogleDriveAuth = () => {
  const dispatch = useAppDispatch();
  // Store pending promises to resolve after login
  const pendingResolveRef = useRef<((token: string) => void) | null>(null);
  const pendingRejectRef = useRef<((error: Error) => void) | null>(null);
  // Store callback for UI updates
  const onAuthChangeRef = useRef<((hasAccess: boolean) => void) | null>(null);

  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      console.log('Google Drive authorization code received:', codeResponse);

      try {
        // Exchange authorization code with backend
        const result = await googleDriveAPI.exchangeCode(codeResponse.code);

        if (!result.success || !result.data) {
          throw new Error(result.message || 'Không thể đổi authorization code');
        }

        // Store access token in cookies
        const accessToken = result.data.access_token;
        const expiresIn = result.data.expires_in;

        setGoogleAccessToken(accessToken, expiresIn);
        setTokenInService(accessToken);
        dispatch(setGoogleDriveConnection({ status: 'connected', connectedEmail: null }));

        toast.success('Đã kết nối Google Drive thành công!');

        // Notify UI about auth change
        if (onAuthChangeRef.current) {
          onAuthChangeRef.current(true);
        }

        // Resolve pending promise if exists
        if (pendingResolveRef.current) {
          pendingResolveRef.current(accessToken);
          pendingResolveRef.current = null;
          pendingRejectRef.current = null;
        }
      } catch (error: any) {
        console.error('Failed to exchange code:', error);
        toast.error('Không thể kết nối Google Drive. Vui lòng thử lại.');

        // Notify UI about auth change
        dispatch(setGoogleDriveConnection({ status: 'required' }));

        if (onAuthChangeRef.current) {
          onAuthChangeRef.current(false);
        }

        // Reject pending promise if exists
        if (pendingRejectRef.current) {
          pendingRejectRef.current(error);
          pendingResolveRef.current = null;
          pendingRejectRef.current = null;
        }
      }
    },
    onError: (error) => {
      console.error('Google Drive auth error:', error);
      toast.error('Không thể kết nối Google Drive. Vui lòng thử lại.');

      // Notify UI about auth change
      dispatch(setGoogleDriveConnection({ status: 'required' }));

      if (onAuthChangeRef.current) {
        onAuthChangeRef.current(false);
      }

      // Reject pending promise if exists
      if (pendingRejectRef.current) {
        pendingRejectRef.current(new Error('Google Drive authorization failed'));
        pendingResolveRef.current = null;
        pendingRejectRef.current = null;
      }
    },
    flow: 'auth-code', // Use authorization code flow
    scope: 'https://www.googleapis.com/auth/drive.file',
    select_account: true,
  });

  /**
   * Check if user has valid Google Drive access token
   */
  const hasAccess = useCallback((): boolean => {
    const token = getGoogleAccessToken();
    return !!token;
  }, []);

  /**
   * Refresh access token using refresh token from backend
   * Returns new access token or null if refresh failed
   */
  const refreshAccessToken = useCallback(async ({ silent = false }: { silent?: boolean } = {}): Promise<string | null> => {
    dispatch(setGoogleDriveConnection({ status: 'refreshing' }));
    try {
      console.log('[useGoogleDriveAuth] refreshAccessToken() called');
      console.log('[useGoogleDriveAuth] Calling SSO API: POST /v1/auth/google-drive/refresh-token');

      // Keep the linked email for the mandatory dialog when refresh fails (for
      // example, when the account linked previously is no longer the owner).
      const status = await googleDriveAPI.getStatus().catch(() => null);
      const connectedEmail = status?.data?.google_email ?? null;
      const result = await googleDriveAPI.refreshToken();
      console.log('[useGoogleDriveAuth] API response:', { success: result.success, messageCode: result.messageCode });

      if (!result.success || !result.data) {
        // Check if user needs to re-authorize
        if (result.messageCode === 'NO_REFRESH_TOKEN' || result.messageCode === 'REFRESH_TOKEN_EXPIRED') {
          console.log('[useGoogleDriveAuth] ⚠️ Refresh token not found or expired. User needs to re-authorize.');
          dispatch(setGoogleDriveConnection({ status: 'required', connectedEmail }));
          if (!silent) toast.info('Vui lòng cấp quyền Google Drive lại để tiếp tục.');
          return null;
        }

        console.error('[useGoogleDriveAuth] ❌ Refresh failed:', result.message);
        dispatch(setGoogleDriveConnection({ status: 'required', connectedEmail }));
        if (!silent) toast.error('Không thể làm mới token. Vui lòng thử lại.');
        return null;
      }

      // Store new access token
      const accessToken = result.data.access_token;
      const expiresIn = result.data.expires_in;

      console.log('[useGoogleDriveAuth] Saving new token to cookies (expires in', expiresIn, 'seconds)');
      setGoogleAccessToken(accessToken, expiresIn);
      setTokenInService(accessToken);
      dispatch(setGoogleDriveConnection({ status: 'connected', connectedEmail }));

      console.log('[useGoogleDriveAuth] ✅ Google Drive access token refreshed successfully');
      return accessToken;
    } catch (error: any) {
      console.error('[useGoogleDriveAuth] ❌ Failed to refresh access token:', error);
      dispatch(setGoogleDriveConnection({ status: 'required' }));
      if (!silent) toast.error('Không thể làm mới token. Vui lòng thử lại.');
      return null;
    }
  }, [dispatch]);

  /**
   * Get current access token or trigger login if not available
   * Automatically refreshes token if expired or expiring soon
   * Returns promise that resolves to access token after user grants permission
   */
  const ensureAccess = useCallback(async (): Promise<string> => {
    const token = getGoogleAccessToken();

    // If we have a valid token that's not expiring soon, use it
    if (token && !isGoogleTokenExpiring()) {
      console.log('Using valid token from cookie');
      return token;
    }

    // Token is expired/expiring or not available - try to refresh via backend
    console.log('Token expired or expiring soon - attempting refresh via backend');
    const refreshedToken = await refreshAccessToken();

    if (refreshedToken) {
      console.log('Token refreshed successfully via backend');
      return refreshedToken;
    }

    // Refresh failed - trigger login flow and wait for result
    console.log('Refresh failed - triggering OAuth popup');
    return new Promise((resolve, reject) => {
      pendingResolveRef.current = resolve;
      pendingRejectRef.current = reject;
      login();
    });
  }, [login, refreshAccessToken]);

  /**
   * Trigger Google Drive login flow with optional callback
   */
  const requestAccess = useCallback((onAuthChange?: (hasAccess: boolean) => void) => {
    if (onAuthChange) {
      onAuthChangeRef.current = onAuthChange;
    }
    login();
  }, [login]);

  /**
   * Revoke Google Drive access
   */
  const revokeAccess = useCallback(async (): Promise<boolean> => {
    try {
      const result = await googleDriveAPI.revoke();

      if (result.success) {
        // Clear local token
        setGoogleAccessToken('', 0);
        setTokenInService('');
        dispatch(setGoogleDriveConnection({ status: 'required', connectedEmail: null }));
        toast.success('Đã thu hồi quyền Google Drive');
        return true;
      }

      throw new Error(result.message || 'Không thể thu hồi quyền');
    } catch (error: any) {
      console.error('Failed to revoke access:', error);
      toast.error('Không thể thu hồi quyền. Vui lòng thử lại.');
      return false;
    }
  }, [dispatch]);

  return {
    hasAccess,
    ensureAccess,
    requestAccess,
    refreshAccessToken,
    revokeAccess,
  };
};
